import React, { useCallback } from 'react';
import { Flex, Square } from '@grubhubprod/gh-cookbook-react';
import { usePaginationPages } from 'src/components/DataTable/components/DataTablePagination/Pagination/hooks/usePaginationPages';
import { PaginationLink } from 'src/components/DataTable/components/DataTablePagination/Pagination/components/PaginationLink';
import { PaginationNavLink } from 'src/components/DataTable/components/DataTablePagination/Pagination/components/PaginationNavLink';
import { PaginationEllipsis } from 'src/components/DataTable/components/DataTablePagination/Pagination/components/PaginationEllipsis';

export interface PaginationProps {
  page: number;
  pageSize: number;
  collectionSize: number;
  pageChange: (page: number) => void;
  disabled?: boolean;
  boundaryLinks?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  collectionSize,
  pageChange,
  disabled = false,
  boundaryLinks = false,
}) => {
  const totalPages = Math.ceil(collectionSize / pageSize);
  const hasPrevious = page > 1;
  const hasNext = page < totalPages;
  const { pages } = usePaginationPages({
    currentPage: page,
    totalPages,
  });

  const handlePageChange = useCallback(
    (newPage: number) => {
      const isNewPage = newPage !== page;
      const isInRange = newPage >= 1 && newPage <= totalPages;
      const isValidPage = !disabled && isNewPage && isInRange;

      if (isValidPage) {
        pageChange(newPage);
      }
    },
    [disabled, page, totalPages, pageChange]
  );

  return (
    <Square as="nav" role="navigation" aria-label="Pagination">
      <Flex alignItems="center">
        {boundaryLinks && (
          <PaginationNavLink
            type="first"
            targetPage={1}
            disabled={disabled || !hasPrevious}
            onClick={handlePageChange}
          />
        )}

        <PaginationNavLink
          type="previous"
          targetPage={page - 1}
          disabled={disabled || !hasPrevious}
          onClick={handlePageChange}
        />

        {pages.map((item) => {
          if (item.type === 'ellipsis') {
            return <PaginationEllipsis key={item.key} />;
          }

          return (
            <PaginationLink
              key={item.key}
              page={item.page}
              isActive={item.page === page}
              disabled={disabled}
              onClick={handlePageChange}
            />
          );
        })}

        <PaginationNavLink
          type="next"
          targetPage={page + 1}
          disabled={disabled || !hasNext}
          onClick={handlePageChange}
        />

        {boundaryLinks && (
          <PaginationNavLink
            type="last"
            targetPage={totalPages}
            disabled={disabled || !hasNext}
            onClick={handlePageChange}
          />
        )}
      </Flex>
    </Square>
  );
};
